class BFCollageConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.variants = JSON.parse(this.querySelector('[data-bf-collage-variants]').textContent);
    this.form = this.querySelector('form');
    this.frame = this.querySelector('[data-bf-collage-frame]');
    this.canvas = this.querySelector('[data-bf-collage-canvas]');
    this.sizeSelect = this.querySelector('[data-bf-collage-size]');
    this.zoomInput = this.querySelector('[data-bf-collage-zoom]');
    this.requiredCount = 2;
    this.tier = '2–4 Photos';
    this.activeSlot = 1;
    this.transforms = Array.from({ length: 20 }, () => ({ zoom: 1, x: 0, y: 0 }));
    this.urls = Array(20).fill('');
    this.pointer = null;
    this.bind();
    this.chooseLayout(this.querySelector('[data-bf-layout][aria-pressed="true"]'));
    this.handleSizeChange();
    this.updateFinish();
  }

  bind() {
    this.querySelectorAll('[data-bf-layout]').forEach(button => button.addEventListener('click', () => this.chooseLayout(button)));
    this.querySelectorAll('[data-bf-photo-input]').forEach(input => input.addEventListener('change', () => this.loadPhoto(input)));
    this.querySelectorAll('[data-bf-slot]').forEach(slot => {
      slot.addEventListener('click', () => this.selectSlot(Number(slot.dataset.bfSlot)));
      slot.addEventListener('pointerdown', event => this.startDrag(event, slot));
      slot.addEventListener('pointermove', event => this.drag(event, slot));
      ['pointerup','pointercancel','pointerleave'].forEach(type => slot.addEventListener(type, () => this.endDrag()));
    });
    this.sizeSelect.addEventListener('change', () => this.handleSizeChange());
    this.zoomInput.addEventListener('input', () => this.setZoom(Number(this.zoomInput.value)));
    this.querySelectorAll('[name="properties[Moulding]"],[name="properties[Mat Board]"]').forEach(input => input.addEventListener('change', () => this.updateFinish()));
    this.form.addEventListener('submit', event => this.submitForm(event));
  }

  chooseLayout(button) {
    if (!button || button.disabled) return;
    this.querySelectorAll('[data-bf-layout]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    this.requiredCount = Number(button.dataset.count);
    this.tier = button.dataset.tier;
    this.canvas.dataset.layout = button.dataset.bfLayout;
    this.form.querySelector('[data-bf-layout-property]').value = button.dataset.label;
    this.querySelector('[data-bf-summary-layout]').textContent = button.dataset.label;
    this.querySelector('[data-bf-tier]').textContent = this.tier;
    this.querySelector('[data-bf-upload-note]').textContent = `Upload ${this.requiredCount} JPEG, PNG or WebP images, maximum 25 MB each.`;
    this.querySelectorAll('[data-bf-upload-row]').forEach((row, index) => {
      const enabled = index < this.requiredCount;
      row.hidden = !enabled;
      const input = row.querySelector('input');
      input.disabled = !enabled;
      input.required = enabled;
    });
    this.querySelectorAll('[data-bf-slot]').forEach((slot, index) => slot.hidden = index >= this.requiredCount);
    if (this.activeSlot > this.requiredCount) this.selectSlot(1);
    this.updateVariant();
    this.updateProgress();
  }

  normalizedSize() {
    return this.sizeSelect.value.toLowerCase().replace(/\s/g,'').replace('×','x');
  }

  allowedTiers() {
    const map = {
      '11x14':['2–4 Photos','5–6 Photos'],
      '16x20':['2–4 Photos','5–6 Photos','7–9 Photos'],
      '18x24':['2–4 Photos','5–6 Photos','7–9 Photos','10–12 Photos','13–16 Photos'],
      '24x36':['2–4 Photos','5–6 Photos','7–9 Photos','10–12 Photos','13–16 Photos','17–20 Photos']
    };
    return map[this.normalizedSize()] || ['2–4 Photos'];
  }

  handleSizeChange() {
    const allowed = this.allowedTiers();
    const buttons = [...this.querySelectorAll('[data-bf-layout]')];
    buttons.forEach(button => {
      button.disabled = !allowed.includes(button.dataset.tier);
      button.setAttribute('aria-disabled', String(button.disabled));
    });
    const selected = buttons.find(button => button.getAttribute('aria-pressed') === 'true');
    if (!selected || selected.disabled) this.chooseLayout(buttons.find(button => !button.disabled));
    else this.updateVariant();
  }

  loadPhoto(input) {
    const index = Number(input.dataset.bfPhotoInput) - 1;
    const file = input.files && input.files[0];
    const label = input.closest('label').querySelector('[data-bf-file-name]');
    if (!file) return;
    if (!['image/jpeg','image/png','image/webp'].includes(file.type) || file.size > 25 * 1024 * 1024) {
      input.value = '';
      label.textContent = 'Use a JPEG, PNG or WebP image up to 25 MB.';
      this.updateProgress();
      return;
    }
    if (this.urls[index]) URL.revokeObjectURL(this.urls[index]);
    this.urls[index] = URL.createObjectURL(file);
    const slot = this.querySelector(`[data-bf-slot="${index + 1}"]`);
    slot.querySelector('img').src = this.urls[index];
    slot.classList.add('has-photo');
    label.textContent = file.name;
    this.selectSlot(index + 1);
    this.updateProgress();
  }

  selectSlot(number) {
    this.activeSlot = number;
    this.querySelectorAll('[data-bf-slot]').forEach(slot => slot.classList.toggle('is-active', Number(slot.dataset.bfSlot) === number));
    const transform = this.transforms[number - 1];
    this.zoomInput.value = transform.zoom;
    this.querySelector('[data-bf-collage-zoom-value]').textContent = `${transform.zoom.toFixed(2)}×`;
  }

  setZoom(value) {
    const transform = this.transforms[this.activeSlot - 1];
    transform.zoom = Math.max(1, Math.min(3, value));
    this.querySelector('[data-bf-collage-zoom-value]').textContent = `${transform.zoom.toFixed(2)}×`;
    this.renderSlot(this.activeSlot);
  }

  startDrag(event, slot) {
    const number = Number(slot.dataset.bfSlot);
    if (!this.urls[number - 1]) return;
    this.selectSlot(number);
    slot.setPointerCapture(event.pointerId);
    const transform = this.transforms[number - 1];
    this.pointer = { id:event.pointerId, x:event.clientX, y:event.clientY, ox:transform.x, oy:transform.y };
  }

  drag(event, slot) {
    if (!this.pointer || event.pointerId !== this.pointer.id) return;
    const transform = this.transforms[this.activeSlot - 1];
    transform.x = this.pointer.ox + event.clientX - this.pointer.x;
    transform.y = this.pointer.oy + event.clientY - this.pointer.y;
    const limitX = slot.clientWidth * (transform.zoom - 1) / 2;
    const limitY = slot.clientHeight * (transform.zoom - 1) / 2;
    transform.x = Math.max(-limitX, Math.min(limitX, transform.x));
    transform.y = Math.max(-limitY, Math.min(limitY, transform.y));
    this.renderSlot(this.activeSlot);
  }

  endDrag() { this.pointer = null; }

  renderSlot(number) {
    const transform = this.transforms[number - 1];
    const image = this.querySelector(`[data-bf-slot="${number}"] img`);
    image.style.transform = `translate3d(${transform.x}px,${transform.y}px,0) scale(${transform.zoom})`;
  }

  updateVariant() {
    const size = this.sizeSelect.value;
    const variant = this.variants.find(item => item.options.includes(size) && item.options.includes(this.tier));
    const error = this.querySelector('[data-bf-collage-error]');
    if (!variant) {
      this.form.querySelector('[data-bf-variant-id]').value = '';
      this.querySelector('[data-bf-summary-price]').textContent = 'Configuration unavailable';
      error.textContent = `Create a Shopify variant for ${size} / ${this.tier}.`;
    } else {
      this.form.querySelector('[data-bf-variant-id]').value = variant.id;
      this.querySelector('[data-bf-summary-price]').textContent = this.formatMoney(variant.price);
      error.textContent = variant.available ? '' : 'This configuration is currently unavailable.';
    }
    this.querySelector('[data-bf-summary-size]').textContent = size;
    this.updateProgress();
  }

  formatMoney(cents) { return new Intl.NumberFormat('en-JM',{style:'currency',currency:'JMD',maximumFractionDigits:0}).format(cents / 100); }

  updateFinish() {
    const moulding = this.querySelector('[name="properties[Moulding]"]:checked');
    const mat = this.querySelector('[name="properties[Mat Board]"]:checked');
    if (moulding) {
      this.frame.style.setProperty('--collage-frame', moulding.dataset.frame);
      this.frame.style.setProperty('--collage-accent', moulding.dataset.accent);
    }
    if (mat) {
      this.frame.style.setProperty('--collage-mat', mat.dataset.mat);
      this.frame.dataset.noMat = String(mat.value === 'No Mat');
    }
  }

  updateProgress() {
    const inputs = [...this.querySelectorAll('[data-bf-photo-input]:not(:disabled)')];
    const uploaded = inputs.filter(input => input.files && input.files[0]).length;
    const variant = this.variants.find(item => String(item.id) === this.form.querySelector('[data-bf-variant-id]').value);
    this.querySelector('[data-bf-summary-count]').textContent = `${uploaded} of ${this.requiredCount} uploaded`;
    this.querySelector('[data-bf-collage-status]').textContent = uploaded === this.requiredCount ? 'All photographs are ready for review.' : `Upload ${this.requiredCount - uploaded} more photograph${this.requiredCount - uploaded === 1 ? '' : 's'} to continue.`;
    this.querySelector('[data-bf-collage-submit]').disabled = uploaded !== this.requiredCount || !variant || !variant.available;
  }

  submitForm(event) {
    if (this.querySelector('[data-bf-collage-submit]').disabled) {
      event.preventDefault();
      return;
    }
    this.form.querySelector('[data-bf-positions]').value = this.transforms.slice(0,this.requiredCount).map((item,index) => `Photo ${index + 1}: ${item.zoom.toFixed(2)}x, X ${Math.round(item.x)}, Y ${Math.round(item.y)}`).join(' | ');
    this.form.querySelector('[data-bf-reference]').value = `BF-COL-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2,8).toUpperCase()}`;
    const submit = this.querySelector('[data-bf-collage-submit]');
    submit.disabled = true;
    submit.textContent = 'Adding your collage…';
  }
}

if (!customElements.get('bf-collage-configurator')) customElements.define('bf-collage-configurator', BFCollageConfigurator);
